"use client";
import React from "react";
import { Box, Typography, Container } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IMAGES } from "@/constants/images";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const RecruitmentTeam = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const rows = t("recruitmentTeam.rows", { returnObjects: true }) || [];

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        py: { xs: 2, sm: 6, md: 3 },
        direction: isArabic ? "rtl" : "ltr",
        overflowX: "hidden",
        width: "100%",
        mt: "3%",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          maxWidth: "940px !important",
          overflowX: "hidden",
        }}
      >
        {/* ── HEADER ── */}
        <Box sx={{ textAlign: "center", mb: { xs: 3, sm: 4, md: 6 } }}>
          {/* Badge */}
          <Box
            sx={{
              display: "inline-block",
              backgroundColor: "#EDE9FE",
              borderRadius: "50px",
              px: 2.2,
              py: 0.9,
              mb: { xs: 2, sm: 3 },
            }}
          >
            <Typography
              sx={{
                fontFamily: "var(--font-outfit)",
                fontSize: { xs: "0.65rem", sm: "0.68rem", md: "0.9rem" },
                fontWeight: 600,
                color: "#8200DB",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {t("recruitmentTeam.badge")}
            </Typography>
          </Box>

          {/* Heading */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 0.8, md: 2 },
              flexWrap: "wrap",
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: "var(--font-plusjakarta)",
                fontWeight: 800,
                fontSize: { xs: "1.5rem", sm: "2.2rem", md: "3.2rem" },
                color: "#E7000B",
                lineHeight: 1.2,
                display: "inline-block",
                backgroundImage:
                  "linear-gradient(180deg, transparent 85%, #ffe4e6 1%)",
              }}
            >
              {t("recruitmentTeam.headingBefore")}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: "var(--font-plusjakarta)",
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.5rem", md: "2.2rem" },
                color: "#99A1AF",
                lineHeight: 1.2,
                mx: { xs: 0.3, md: 1 },
              }}
            >
              vs
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: "var(--font-plusjakarta)",
                fontWeight: 800,
                fontSize: { xs: "1.5rem", sm: "2.2rem", md: "3.2rem" },
                color: "#9810FA",
                lineHeight: 1.2,
                display: "inline-block",
                backgroundImage:
                  "linear-gradient(180deg, transparent 85%, #f3e8ff 1%)",
              }}
            >
              {t("recruitmentTeam.headingAfter")}
            </Typography>
          </Box>
        </Box>

        {/* ── TABLE ── */}
        <Box
          sx={{
            borderRadius: { xs: "12px", md: "16px" },
            // On xs/sm: allow horizontal scroll inside this wrapper
            overflowX: { xs: "auto", md: "hidden" },
            overflowY: "visible",
            backgroundColor: "#ffffff",
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.04)",
            border: "1px solid #f3f4f6",
            // hide scrollbar visually but keep functional
            "&::-webkit-scrollbar": {
              height: { xs: "3px", sm: "4px" },
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f5f9",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#c7b8f5",
              borderRadius: "4px",
            },
          }}
        >
          {/* Inner wrapper — min width so table doesn't collapse on mobile */}
          <Box sx={{ minWidth: { xs: "480px", sm: "560px", md: "unset" } }}>
            {/* Table Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1.2fr 1fr 1fr",
                px: { xs: 2, sm: 3, md: 5 },
                py: { xs: 1.8, sm: 2, md: 2.5 },
                backgroundColor: "#F9F3FF",
                borderBottom: "1px solid #f3f4f6",
              }}
            >
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontWeight: 600,
                  fontSize: { xs: "0.78rem", sm: "0.88rem", md: "1rem" },
                  color: "#364153",
                }}
              >
                {t("recruitmentTeam.colTask")}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontWeight: 600,
                  fontSize: { xs: "0.78rem", sm: "0.88rem", md: "1rem" },
                  color: "#FB2C36",
                }}
              >
                {t("recruitmentTeam.colBefore")}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontWeight: 600,
                  fontSize: { xs: "0.78rem", sm: "0.88rem", md: "1rem" },
                  color: "#9810FA",
                }}
              >
                {t("recruitmentTeam.colAfter")}
              </Typography>
            </Box>

            {/* Table Rows */}
            {Array.isArray(rows) &&
              rows.map((row, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1.2fr 1fr 1fr",
                    px: { xs: 2, sm: 3, md: 5 },
                    py: { xs: 1.6, sm: 2, md: 2.2 },
                    borderBottom:
                      i < rows.length - 1 ? "1px solid #f9fafb" : "none",
                    backgroundColor: "#ffffff",
                    transition: "background-color 0.15s ease",
                  }}
                >
                  {/* Task */}
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                      color: row.highlight ? "#9048FB" : "#384054",
                      fontWeight: 500,
                      pr: 1,
                      wordBreak: "break-word",
                    }}
                  >
                    {row.task}
                  </Typography>

                  {/* Before */}
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                      color: "#4A5565",
                      fontWeight: 400,
                      pr: 1,
                      wordBreak: "break-word",
                    }}
                  >
                    {row.before}
                  </Typography>

                  {/* After */}
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.95rem" },
                      color: row.afterGreen ? "#00A63E" : "#101828",
                      fontWeight: 500,
                      wordBreak: "break-word",
                    }}
                  >
                    {row.after}
                  </Typography>
                </Box>
              ))}
          </Box>
        </Box>

        {/* ── BOTTOM NOTE + BOOK A DEMO ── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 3, sm: 4 },
            mt: { xs: 3, sm: 4, md: 6 },
            px: { xs: 1, sm: 0 },
          }}
        >
          {/* Bottom Note */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 1, sm: 1.5 },
              flexWrap: "wrap",
            }}
          >
            <Box
              component="img"
              src={IMAGES.AWESOME_IMG}
              alt="Awesome Effect"
              sx={{
                width: { xs: "20px", sm: "22px", md: "24px" },
                height: { xs: "20px", sm: "22px", md: "24px" },
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: { xs: 0.8, sm: 1 },
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontWeight: 800,
                  fontSize: { xs: "0.82rem", sm: "0.9rem", md: "1.05rem" },
                  color: "#101828",
                  backgroundColor: "#f4effa",
                  px: { xs: 1.5, sm: 2 },
                  py: 0.6,
                  borderRadius: "6px",
                  display: "inline-block",
                }}
              >
                {t("recruitmentTeam.bottomBold")}
              </Box>
              <Typography
                component="span"
                sx={{
                  fontFamily: "var(--font-outfit)",
                  fontSize: { xs: "0.82rem", sm: "0.9rem", md: "1.05rem" },
                  color: "#101828",
                  fontWeight: 400,
                  textAlign: { xs: "center", sm: "left" },
                }}
              >
                {t("recruitmentTeam.bottomText")}
              </Typography>
            </Box>
          </Box>

          {/* Book a Demo Button */}
          <Box
            component="a"
            id="btn-six"
            // onClick={() => {
            //   const el = document.getElementById("book-demo-section");
            //   if (el) el.scrollIntoView({ behavior: "smooth" });
            // }}
             onClick={() => {
  window.dispatchEvent(new Event("open-book-demo-modal"));
}}
            sx={{
              mt: 2,
              display: "inline-flex",
              alignItems: "center",
              gap: 1.2,
              backgroundColor: "#9810FA",
              color: "#ffffff",
              fontFamily: "var(--font-outfit)",
              fontWeight: 700,
              fontSize: { xs: "0.78rem", sm: "0.85rem", md: "0.9rem" },
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              px: { xs: 3.5, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              borderRadius: "50px",
              textDecoration: "none",
              cursor: "pointer",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                background: "#9810FA",
                boxShadow: "0 4px 15px rgba(152, 16, 250, 0.4)",
              },
            }}
          >
            {t("recruitmentTeam.cta")}
            <ArrowForwardIcon
              sx={{
                fontSize: "1.3rem",
                transform: isArabic ? "rotate(180deg)" : "none",
              }}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default RecruitmentTeam;
